import { Link } from "react-router";
import { useConfig } from "../components/Shell.tsx";
import { consentStore, reopenConsent } from "../lib/consent.ts";

export function NotFoundPage({ what = "page" }: { what?: string }) {
  return (
    <div className="sheet sheet--narrow emptystate">
      <div className="emptystate__slot" aria-hidden="true" />
      <h1 className="page-title">This {what} got peeled off</h1>
      <p className="lede">It doesn't exist, or it isn't available any more.</p>
      <div className="emptystate__actions">
        <Link to="/" className="btn">
          Back to the sheet
        </Link>
      </div>
    </div>
  );
}

export function HelpPage() {
  const cfg = useConfig();
  return (
    <div className="sheet sheet--narrow prose">
      <h1 className="page-title">Shipping &amp; returns</h1>
      <h2>Made to order</h2>
      <p>
        Every item is printed for you after you order, by our print-on-demand partners. Production
        usually takes a few business days, then it ships with the delivery option you chose at
        checkout.
      </p>
      <h2>Shipping costs</h2>
      <p>Shipping is calculated for your address at checkout and shown before you pay.</p>
      <h2>Tracking</h2>
      <p>
        When your order ships we email you a tracking link. Your order page always shows the latest
        status.
      </p>
      <h2>Returns</h2>
      <p>
        Shop designs can be returned within 14 days of delivery, for any reason. Items made from
        your own Studio designs are made to your specifications, so we can't take them back for
        change of mind or wrong size. The details are in our <Link to="/terms">terms of sale</Link>.
      </p>
      <h2>Something wrong with your order?</h2>
      <p>
        If an item arrives damaged or misprinted, email us with your order number and a photo and
        we'll reprint it or refund you.
      </p>
      {cfg.data && (
        <p>
          Contact: <a href={`mailto:${cfg.data.supportEmail}`}>{cfg.data.supportEmail}</a>
        </p>
      )}
    </div>
  );
}

const COOKIES: Array<{ name: string; kind: string; purpose: string; lasts: string }> = [
  {
    name: "mu_session",
    kind: "Cookie, essential",
    purpose: "Keeps you signed in after you use an email sign-in link.",
    lasts: "30 days",
  },
  {
    name: "mu_anon",
    kind: "Cookie, essential",
    purpose: "Remembers the designs you make before signing in, so they stay yours.",
    lasts: "1 year",
  },
  {
    name: "memeup.cart.v1",
    kind: "Browser storage, essential",
    purpose: "Remembers what's in your bag.",
    lasts: "Until you check out or clear it",
  },
  {
    name: "memeup.consent.v1",
    kind: "Browser storage, essential",
    purpose: "Remembers your cookie choice.",
    lasts: "Until you change it",
  },
  {
    name: "PayPal",
    kind: "Third-party cookies, essential",
    purpose: "Set by PayPal on the checkout page to take payment and prevent fraud.",
    lasts: "See PayPal's cookie policy",
  },
  {
    name: "_fbp (Meta Pixel)",
    kind: "Cookie, optional analytics",
    purpose:
      "Only if you accept all cookies. Tells us which Instagram and Facebook ads bring visitors and sales.",
    lasts: "90 days",
  },
];

export function CookiesPage() {
  const cfg = useConfig();
  const consent = consentStore.use((c) => c);
  return (
    <div className="sheet sheet--narrow prose">
      <h1 className="page-title">Cookies</h1>
      <p>
        memeup uses what the shop needs to work: signing in, keeping your designs and bag, and
        taking payment. The one optional cookie is the Meta Pixel, which tells us which of our
        Instagram and Facebook ads work. It only loads if you choose "Accept all", and you can
        change your mind at any time below.
      </p>
      <div className="tablewrap">
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Type</th>
              <th>What it's for</th>
              <th>How long</th>
            </tr>
          </thead>
          <tbody>
            {COOKIES.map((c) => (
              <tr key={c.name}>
                <td>
                  <code>{c.name}</code>
                </td>
                <td>{c.kind}</td>
                <td>{c.purpose}</td>
                <td>{c.lasts}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <h2>Your choice</h2>
      <p>
        {consent.decidedAt
          ? `You chose ${consent.analytics ? "to accept all cookies" : "essential cookies only"} on ${new Date(consent.decidedAt).toLocaleDateString()}.`
          : "You haven't made a choice yet."}{" "}
        <button type="button" className="linkbtn" onClick={reopenConsent}>
          Change your cookie settings
        </button>
      </p>
      {cfg.data && (
        <p>
          Questions: <a href={`mailto:${cfg.data.supportEmail}`}>{cfg.data.supportEmail}</a>
        </p>
      )}
    </div>
  );
}
